<template>
  <v-row justify="center">
    <v-dialog
      origin="top center"
      v-model="dialogStatus"
      persistent
      content-class="serial-number-dialog"
      :max-width="dialogWidth"
    >
      <v-card>
        <v-card-title
          class="headline dialog-custom-header white-background custom-border-bottom custom-header-blue-text"
        >
          <template v-if="serialNumberOption.id"> Update Inventory </template>
          <template v-else> Add Inventory </template>
          - &nbsp;<span class="orange--text">{{ productInfo.barcode }}</span>
        </v-card-title>
        <v-card-text class="p-8 font-size-16">
          <perfect-scrollbar
            :options="{ suppressScrollX: true }"
            class="scroll"
            style="max-height: calc(100vh - 280px); position: relative"
          >
            <v-form
              ref="serialNumberForm"
              v-model.trim="formValid"
              lazy-validation
              v-on:submit.stop.prevent="update_or_create"
            >
              <v-container fluid>
                <v-row>
                  <!-- Product Information Display -->
                  <v-col
                    md="12"
                    class="py-0 mb-4"
                    v-if="productInfo && productInfo.barcode"
                  >
                    <v-layout
                      class="nmt-1 bg-quo pb-3 custom-border-bottom"
                      style="margin-bottom: 2rem"
                    >
                      <v-flex md2 class="mr-2">
                        <table width="100%">
                          <tr>
                            <td class="py-1">
                              <v-avatar size="80">
                                <ImageNew
                                  :images="productInfo.product_images"
                                  :parent="productInfo.id"
                                  type="service"
                                  width="80"
                                  height="80"
                                  :isPageLoading="pageLoading"
                                >
                                </ImageNew>
                              </v-avatar>
                            </td>
                          </tr>
                        </table>
                      </v-flex>
                      <v-flex md4 class="mr-2">
                        <table width="100%" class="mt-4">
                          <tr v-if="productInfo.barcode">
                            <td class="py-0">
                              <label
                                ><span class="font-weight-600">ID: </span>
                                <span>{{ productInfo.barcode }}</span></label
                              >
                            </td>
                          </tr>
                          <tr v-if="productInfo.name">
                            <td class="py-0">
                              <label
                                ><span class="font-weight-600">Name: </span>
                                <span>{{ productInfo.name }}</span></label
                              >
                            </td>
                          </tr>
                        </table>
                      </v-flex>
                      <v-flex md6 class="mr-2">
                        <table width="100%" class="mt-4">
                          <tr>
                            <td class="py-0" v-if="productInfo.brand">
                              <label
                                ><span class="font-weight-600">Model: </span>
                                <span>{{ productInfo.brand }}</span></label
                              >
                            </td>
                            <td class="py-0" v-else>
                              <em class="text-muted ml-2">no model</em>
                            </td>
                          </tr>
                          <tr>
                            <td class="py-0" v-if="productInfo.category">
                              <label
                                ><span class="font-weight-600">Category: </span>
                                <span>{{ productInfo.category }}</span></label
                              >
                            </td>
                            <td class="py-0" v-else>
                              <em class="text-muted ml-2">no category</em>
                            </td>
                          </tr>
                        </table>
                      </v-flex>
                    </v-layout>
                  </v-col>

                  <v-col md="6" class="py-0">
                    <label class="font-weight-600 my-1">Quantity</label>
                    <v-text-field
                      dense
                      filled
                      color="cyan"
                      placeholder="Quantity"
                      solo
                      flat
                      v-model.trim="serialNumberOption.initial_stock"
                      :disabled="pageLoading"
                      :maxLength="5"
                    ></v-text-field>
                  </v-col>

                  <v-col md="6" class="py-0">
                    <label class="font-weight-600 my-1">Purchase Date</label>
                    <DatePicker
                      :defaultDate="serialNumberOption.purchase_date"
                      solo
                      :pageLoading="pageLoading"
                      :placeholder="'Purchase Date'"
                      v-model="serialNumberOption.purchase_date"
                    ></DatePicker>
                  </v-col>

                  <v-col md="6" class="py-0">
                    <label class="font-weight-600 my-1">Purchase Order </label>
                    <v-text-field
                      dense
                      filled
                      color="cyan"
                      placeholder="Purchase Order "
                      solo
                      flat
                      v-model.trim="serialNumberOption.inbound_doc"
                      :disabled="pageLoading"
                    ></v-text-field>
                  </v-col>

                  <v-col md="6" class="py-0">
                    <label class="font-weight-600 my-1">Inbound Date</label>
                    <DatePicker
                      :defaultDate="serialNumberOption.inbound_date"
                      solo
                      :pageLoading="pageLoading"
                      :placeholder="'Inbound Date'"
                      v-model="serialNumberOption.inbound_date"
                    ></DatePicker>
                  </v-col>

                  <v-col md="12" class="py-0">
                    <label class="font-weight-600 my-1">Remarks</label>
                    <v-textarea
                      class="required-field"
                      dense
                      filled
                      label="Remarks"
                      solo
                      flat
                      row-height="20"
                      auto-grow
                      :disabled="pageLoading"
                      color="cyan"
                      v-model.trim="serialNumberOption.remarks"
                      :rules="[
                        validateRules.minLength(
                          serialNumberOption.remarks,
                          'Notes',
                          1
                        ),
                        validateRules.maxLength(
                          serialNumberOption.remarks,
                          'Notes',
                          200
                        ),
                      ]"
                      v-on:keypress="(e) => manageLimitdescr(e)"
                      v-on:paste="(e) => onPaste(e)"
                    >
                    </v-textarea>
                    <v-col md="12" class="text-right">
                      {{
                        serialNumberOption.remarks
                          ? serialNumberOption.remarks?.length
                          : 0
                      }}/200
                    </v-col>
                  </v-col>
                </v-row>
              </v-container>
            </v-form>
          </perfect-scrollbar>
        </v-card-text>
        <v-divider class="my-0"></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <div class="serial-number-manage-action">
            <v-btn
              depressed
              :loading="pageLoading"
              :disabled="pageLoading"
              v-on:click="$emit('close:dialog')"
              class="mx-2 custom-grey-border custom-bold-button"
            >
              Cancel
            </v-btn>
            <v-btn
              depressed
              :disabled="!formValid || pageLoading"
              :loading="pageLoading"
              v-on:click="update_or_create"
              class="mx-2 custom-bold-button white--text"
              color="cyan"
            >
              Save
            </v-btn>
          </div>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<style scoped>
.v-dialog__content {
  align-items: start;
}

.v-autocomplete:not(.v-input--is-focused).v-select--chips input {
  max-height: 18px;
  padding: 0;
}
</style>

<script>
import moment from "moment";
import DatePicker from "@/view/pages/partials/Datepicker.vue";
import { POST, GET, CLEAR_ERROR } from "@/core/services/store/request.module";
import CommonMixin from "@/core/plugins/common-mixin";
import ValidationMixin from "@/core/plugins/validation-mixin";
import { cloneDeep } from "lodash";
import ImageNew from "@/view/pages/partials/Detail/ImageNew";
import { v4 as uuidv4 } from "uuid";
import { ErrorEventBus } from "@/core/lib/message.lib";

export default {
  name: "create-or-update-serial-number",
  mixins: [ValidationMixin, CommonMixin],
  props: {
    dialogStatus: {
      type: Boolean,
      default: false,
    },
    serialNumberDetail: {
      type: Object,
      default: () => {
        return new Object();
      },
    },
    productId: {
      type: Number,
      default: 0,
    },

    serialized: {
      type: Number,
      default: 0,
    },
  },
  components: {
    DatePicker,
    ImageNew,
  },
  watch: {
    "serialNumberOption.initial_stock"() {
      if (
        this.serialNumberOption.initial_stock ==
        this.serialNumberOption?.searial_numbers?.length
      ) {
        this.serialNumberOption.searial_numbers.forEach((item) => {
          item.editable = false;
        });
      } else if (
        this.serialNumberOption.initial_stock >
        this.serialNumberOption?.searial_numbers?.length
      ) {
        this.serialNumberOption.searial_numbers.forEach((item) => {
          item.editable = false;
        });
      } else if (
        this.serialNumberOption.initial_stock <
        this.serialNumberOption?.searial_numbers?.length
      ) {
        this.serialNumberOption.searial_numbers.forEach((item, index) => {
          if (index >= this.serialNumberOption.initial_stock) {
            item.editable = true;
          } else {
            item.editable = false;
          }
        });
      }
    },
    dialogStatus: {
      deep: true,
      immediate: true,
      handler(param) {
        if (param) {
          // this.getProductDetail();
          this.initializeData();
        }
      },
    },
  },
  data() {
    return {
      formValid: true,
      pageLoading: false,
      importDialog: false,
      activeRowIndex: null,
      serialNumberOption: {
        id: null,
        product_id: 0,
        initial_stock: 0,
        purchase_date: moment().format("YYYY-MM-DD"),
        inbound_date: moment().format("YYYY-MM-DD"),
        inbound_doc: null,
        remarks: null,
      },
      productInfo: {},
    };
  },
  methods: {
    manageLimitdescr(e) {
      this.errorMessage = null;
      if (
        this.serialNumberOption.remarks &&
        this.serialNumberOption.remarks.length > 199
      ) {
        e.preventDefault();
      }
    },
    onPaste(e) {
      let coppied = e.clipboardData.getData("Text");
      let preVal = this.serialNumberOption.remarks;
      let finalval = String(preVal) + String(coppied);
      if (Number(finalval.length) > 199) {
        let trimValue = finalval.substring(0, 200);
        this.serialNumberOption.remarks = trimValue;
        e.preventDefault();
      }
    },
    clickOutside() {
      this.activeRowIndex = null;
    },
    getProductDetail() {
      const _this = this;
      _this.pageLoading = true;
      _this.$store
        .dispatch(GET, {
          url: "product/" + _this.productId,
        })
        .then(({ data }) => {
          _this.productInfo = data;
        })
        .catch((error) => {
          _this.logError(error);
        })
        .finally(() => {
          _this.pageLoading = false;
        });
    },
    initializeData() {
      this.serialNumberOption = {
        id: null,
        product_id: this.productId,
        initial_stock: 0,
        purchase_date: moment().format("YYYY-MM-DD"),
        inbound_date: moment().format("YYYY-MM-DD"),
        inbound_doc: null,
        remarks: null,
      };
      if (!this.lodash.isEmpty(this.serialNumberDetail)) {
        this.serialNumberOption = {
          ...this.serialNumberOption,
          ...cloneDeep(this.serialNumberDetail),
        };
      }
    },

    checkCodes(row, rowindex) {
      // Check for duplicate serial numbers
      const duplicates = this.serialNumberOption.searial_numbers.filter(
        (item, index) => index !== rowindex && item.text === row.text
      );

      if (duplicates.length > 0) {
        row.error = true;
      } else {
        row.error = false;
      }
    },
    deleteNumber(index) {
      this.serialNumberOption.searial_numbers.splice(index, 1);
    },
    isNumber(event) {
      const charCode = event.which ? event.which : event.keyCode;
      if (charCode > 31 && (charCode < 48 || charCode > 57)) {
        event.preventDefault();
      }
    },

    addingFieldsManually() {
      if (this.serialNumberOption?.initial_stock) {
        this.serialNumberOption.searial_numbers = [];
        for (let i = 1; i <= this.serialNumberOption.initial_stock; i++) {
          let newCode = {
            uuid: uuidv4(),
            text: "",
            editable: false,
            error: false,
            isManual: 1,
          };

          this.serialNumberOption.searial_numbers.push(newCode);
        }
        this.preNumbers = this.lodash.cloneDeep(
          this.serialNumberOption.searial_numbers
        );
      } else {
        ErrorEventBus.$emit(
          "update:error",
          "Please set the initial stock quantity first."
        );
      }
    },

    update_or_create() {
      const _this = this;
      if (!_this.$refs.serialNumberForm.validate()) {
        return false;
      }

      _this.$store.dispatch(CLEAR_ERROR, {});
      _this.pageLoading = true;

      const isEdit = !!_this.serialNumberOption.id;

      let formData = new FormData();
      if (!isEdit) {
        formData.append("product_id", _this.serialNumberOption.product_id);
      }
      formData.append("quantity", _this.serialNumberOption.initial_stock || 0);

      if (_this.serialNumberOption.purchase_date) {
        formData.append(
          "purchase_date",
          _this.serialNumberOption.purchase_date
        );
      }
      if (_this.serialNumberOption.inbound_date) {
        formData.append("inbound_date", _this.serialNumberOption.inbound_date);
      }
      if (_this.serialNumberOption.inbound_doc) {
        formData.append("inbound_doc", _this.serialNumberOption.inbound_doc);
      }
      formData.append("remarks", _this.serialNumberOption.remarks || "");

      let requestURL = isEdit
        ? `non-serial-item/${_this.serialNumberOption.id}`
        : `product/${this.productId}/add-inventory`;

      _this.$store
        .dispatch(POST, { url: requestURL, data: formData })
        .then(() => {
          _this.$emit("refresh:inventory", true);
          _this.$emit("close:dialog");
        })
        .catch((error) => {
          console.log({ error });
        })
        .finally(() => {
          _this.pageLoading = false;
        });
    },
  },
  computed: {
    dialogWidth() {
      return this.lodash.toSafeInteger((document.body.clientWidth / 100) * 60);
    },
  },
  mounted() {
    this.getProductDetail();
  },
};
</script>
